<script setup>
import { computed, defineProps, defineEmits } from 'vue';
import CrossCloseNotifySvg from '@/components/UI/svg/CrossCloseNotifySvg.vue';

const props = defineProps({
    title: {
        type: String,
        default: '',
    },
    subtitle: {
        type: String,
        default: '',
    },
    showHeader: {
        type: Boolean,
        default: true,
    },
    showClose: {
        type: Boolean,
        default: true,
    },
    showNextStep: {
        type: Boolean,
        default: false,
    },
    nextStepEnabled: {
        type: Boolean,
        default: false,
    },
    nextStepLabel: {
        type: String,
        default: 'Следующий шаг',
    },
    contentClass: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['change-step', 'next-step']);

const hasHeader = computed(() => {
    return props.showHeader && (Boolean(props.title) || Boolean(props.subtitle));
});

function closeCard() {
    emit('change-step');
}

function goNextStep() {
    if (!props.nextStepEnabled) {
        return;
    }

    emit('next-step');
}
</script>

<template>
    <div class="relative w-full">
        <div
            class="w-full flex flex-col justify-center items-start bg-white rounded-3xl shadow-xl px-4 sm:px-6 lg:px-10"
            :class="contentClass || (showNextStep ? 'pb-16 sm:pb-20' : 'py-6 sm:py-8')"
        >
            <div
                v-if="hasHeader"
                class="flex items-center justify-between w-full"
            >
                <div class="flex items-center h-20 sm:h-24 lg:h-32">
                    <div
                        v-if="$slots.icon"
                        class="mr-2 sm:mr-4 size-12 sm:size-16 lg:size-20 flex items-center justify-center"
                    >
                        <slot name="icon" />
                    </div>
                    <div>
                        <div class="font-bold text-lg sm:text-xl lg:text-2xl">
                            {{ title }}
                        </div>
                        <div v-if="subtitle" class="text-sm sm:text-base lg:text-lg">
                            {{ subtitle }}
                        </div>
                    </div>
                </div>

                <button
                    v-if="showClose"
                    type="button"
                    class="size-8 sm:size-10 flex items-center justify-center rounded-full hover:bg-gray-200 cursor-pointer transition-all duration-150"
                    aria-label="Закрыть"
                    @click="closeCard"
                >
                    <CrossCloseNotifySvg class="size-6 sm:size-8" />
                </button>
            </div>

            <div class="w-full">
                <slot />
            </div>
        </div>

        <template v-if="showNextStep">
            <button
                v-if="nextStepEnabled"
                type="button"
                class="absolute bottom-5 right-5 z-30 px-4 sm:px-5 py-2 sm:py-3 rounded-full bg-blue-500 hover:bg-blue-600 duration-150 transition-all cursor-pointer select-none font-bold text-white -translate-x-1/2 -translate-y-1/2 text-sm sm:text-base whitespace-nowrap"
                @click="goNextStep"
            >
                {{ nextStepLabel }}
            </button>
            <div
                v-else
                class="absolute bottom-5 right-5 z-30 px-4 sm:px-5 py-2 sm:py-3 rounded-full bg-gray-400 text-gray-600 select-none font-bold -translate-x-1/2 -translate-y-1/2 text-sm sm:text-base whitespace-nowrap cursor-default"
            >
                {{ nextStepLabel }}
            </div>
        </template>
    </div>
</template>

<style scoped>
.relative {
    isolation: isolate;
}
</style>
